import { useEffect, useState } from "react";
import dynamic from "next/dynamic";
import Link from "next/link";
import { useRouter } from "next/router";
import { Collapse, NavDropdown } from "react-bootstrap";
import { FaHeadphonesAlt, FaSearch } from "react-icons/fa";
import Image from "@/components/Image";
import { useUser } from "@/components/contexts/user";
import Player from "./player";
import Overlay from "./MobileLayout/Nav";

import config from "src/config";
import styles from "./Navbar.module.css";
import SearchBar from "./search";

const UserDropdown = dynamic(() => import("./user-dropdown"), { ssr: false });

// The homepage hero's wordmark, and the height of the fixed nav above it.
const HERO_MARK_ID = "home-hero-title";
const NAV_HEIGHT = 90;

function Navbar() {
  const router = useRouter();
  const user = useUser();
  const [displayPlayer, setDisplayPlayer] = useState(true);
  const [displaySearch, setDisplaySearch] = useState(false);
  const [displayOverlay, setDisplayOverlay] = useState(false);

  // The homepage hero shows the wordmark at full size, so the small one in the
  // nav is redundant while that is on screen. It fades in once the hero mark
  // scrolls away, which is where the nav has to carry the identity itself.
  // Pages without a hero never hide it.
  const [brandHidden, setBrandHidden] = useState(router.pathname === "/");

  useEffect(() => {
    const heroMark = document.getElementById(HERO_MARK_ID);
    if (!heroMark || typeof IntersectionObserver === "undefined") {
      setBrandHidden(false);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => setBrandHidden(entry.isIntersecting),
      // Treat the mark as gone once it slides under the fixed nav rather than
      // when it clears the bottom of the window.
      { rootMargin: `-${NAV_HEIGHT}px 0px 0px 0px` }
    );

    observer.observe(heroMark);
    return () => observer.disconnect();
  }, [router.pathname]);

  const togglePlayer = () =>
    setDisplayPlayer((displayPlayer) => !displayPlayer);
  const toggleSearch = () =>
    setDisplaySearch((displaySearch) => !displaySearch);
  const toggleDisplayOverlay = () =>
    setDisplayOverlay((displayOverlay) => !displayOverlay);

  return (
    <nav
      className={`navbar navbar-expand-lg navbar-dark fixed-top ${styles.navbar} flex-column py-0 shadow`}
    >
      <div className="container border-bottom px-0">
        {displayOverlay ? (
          <button
            type="button"
            aria-label="Close navigation"
            className="border-0 p-1 shadow-none navbar-toggler btn-close"
            onClick={toggleDisplayOverlay}
            style={{ width: 30, height: 30 }}
          ></button>
        ) : (
          <button
            type="button"
            aria-label="Toggle navigation"
            className="border-0 p-1 shadow-none navbar-toggler"
            onClick={toggleDisplayOverlay}
          >
            <span className="navbar-toggler-icon" />
          </button>
        )}
        <Link
          href="/"
          className={`d-flex p-0 mx-auto me-lg-3 navbar-brand ${styles.brand}${
            brandHidden ? ` ${styles.brandHidden}` : ""
          }`}
        >

          <Image
            width="60px"
            height="35px"
            layout="fixed"
            src="/static/main/images/logo-yellow.png"
            alt="91.7 KVRX"
            priority={true}
          />

        </Link>
        <div className="navbar-collapse collapse">
          <div className="navbar-nav w-100">
            {config.links.map((link, i) => {
              if (link.dropdown)
                return (
                  <NavDropdown key={i} title={link.title.toUpperCase()}>
                    {link.dropdown.map((link, j) => (
                      <Link href={link.href} key={j} passHref legacyBehavior>
                        <NavDropdown.Item
                          className={`text-uppercase bg-transparent nav-link${
                            (link.exact && router.pathname === link.href) ||
                            (!link.exact && router.pathname.includes(link.href))
                              ? " active"
                              : ""
                          }`}
                        >
                          {link.title}
                        </NavDropdown.Item>
                      </Link>
                    ))}
                  </NavDropdown>
                );

              return (
                <Link
                  key={i}
                  href={link.href}
                  className={`text-uppercase nav-link${
                    (link.exact && router.pathname === link.href) ||
                    (!link.exact && router.pathname.includes(link.href))
                      ? " active"
                      : ""
                  }`}>

                  {link.title}

                </Link>
              );
            })}
            <div className="nav-item ms-lg-auto">
              <a
                className="nav-link"
                target="_blank"
                rel="noopener noreferrer"
                href="https://give.utexas.edu/campaigns/87061/donations/new?amt=100"
              >
                DONATE
              </a>
            </div>
          </div>
        </div>
        <div
          onClick={togglePlayer}
          className="btn btn-link border-start rounded-0 text-white"
        >
          <FaHeadphonesAlt />
        </div>
        <div
          onClick={toggleSearch}
          className="btn btn-link border-start rounded-0 text-white"
        >
          <FaSearch />
        </div>
        {user.isAuthenticated && <UserDropdown user={user} />}
      </div>
      <header className="w-100">
        <div className="container px-0">
          <Collapse in={displayPlayer}>
            <div className="border-bottom">
              <Player />
            </div>
          </Collapse>
          <Collapse in={displaySearch}>
            <div className="border-bottom">
              <SearchBar />
            </div>
          </Collapse>
        </div>
      </header>
      <Overlay
        display={displayOverlay}
        toggle={toggleDisplayOverlay}
        otherHeight={39 + (displayPlayer ? 51 : 0) + (displaySearch ? 36 : 0)}
      />
    </nav>
  );
}

export default Navbar;
